import GenericDB from './generic-db';

export default class FundingSourcesDB extends GenericDB {
  constructor() {
    super('fundingSources');
  }

  /**
   * Clinic-scoped by a `clinic` field: callers filter reads with a ['clinic', '==', clinicId]
   * constraint and set `clinic` on writes.
   *
   * The constraint is REQUIRED rather than injected, and the reason is that omitting it used to
   * be harmless. As a `clinicSettings/{clinicId}/fundingSources` subcollection the path did the
   * scoping, so an unconstrained `readAll()` was correct — and two call sites relied on that.
   * Flattened to a root collection the same call reaches every clinic's funding sources.
   *
   * Injecting the clinic here instead would mean handing the id back to the constructor, which is
   * exactly what the root-collection move removed, and would diverge from how treatmentServices
   * and placesOfService are read. Requiring it keeps the call shape identical to those siblings
   * while making the omission impossible to ship.
   *
   * Failing loudly is the point. The security rules already refuse an unscoped read (Firestore
   * rejects a query it cannot prove is entirely readable), so on the client the omission surfaces
   * as an opaque permissions error rather than as leaked data. This turns that into an error that
   * names the actual mistake. It does NOT protect the admin SDK, where rules do not apply and the
   * query is the only scoping there is — see the backend config repository, which states the same
   * rule for itself.
   *
   * @param {Array<Array>} constraints - must include a `clinic` constraint
   */
  readAll(constraints = null, ...rest) {
    const isClinicScoped = Array.isArray(constraints)
      && constraints.some((constraint) => constraint?.[0] === 'clinic');

    if (!isClinicScoped) {
      throw new Error(
        "fundingSources is a root collection: reads must pass a ['clinic', '==', clinicId] constraint.",
      );
    }

    return super.readAll(constraints, ...rest);
  } // readAll()
}
